"use client"

import { CardFooter } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ExternalLink, Heart, MessageCircle } from "lucide-react"
import DeleteCommentButton from "./DeleteCommentButton"
import EditCommentButton from "./EditCommentButton"
import EditComment from "./EditComment"
import AddComment from "./AddComment"
import { CommentProps } from "./types"
import Link from "next/link"
import { MAX_DEPTH } from "./consts"

export default function CommentFooter(props: CommentProps) {
  const {
    comment,
    depth,
    postId,
    refresh,
    isEditingCommentId,
    handleSetIsEditingCommentId,
    isEditInProgress,
    handleEditStart,
    handleEditEnd,
    isReplyingToCommentId,
    handleSetIsReplyingToCommentId,
    isReplyInProgress,
    handleReplyStart,
    handleReplyEnd,
  } = props

  return (
    <CardFooter className="ml-6 pb-0 flex flex-col items-start">
      <div className="flex flex-row">
        {/* Currently, only Farcaster supports likes. */}
        {comment.platform === "farcaster" && (
          <Button
            variant="ghost"
            className="px-2 hover:bg-transparent hover:cursor-default"
          >
            <Heart className="w-4 h-4 text-muted-foreground opacity-70" />
            <span className="sr-only">Like</span>
            <span className="text-xs ml-1 text-muted-foreground">
              {comment.farcasterLikesCount || 0}
            </span>
          </Button>
        )}

        {/* Currently, only Paragraph supports replies. */}
        {/* Don't allow a user to hit the reply button if they're at the max depth. */}
        {depth < MAX_DEPTH && comment.platform === "paragraph" && (
          <Button
            variant="ghost"
            className="px-2 mr-2"
            onClick={() => handleSetIsReplyingToCommentId(comment.id)}
            disabled={isReplyInProgress}
          >
            <MessageCircle className="w-4 h-4 text-muted-foreground opacity-70" />
            <span className="sr-only">Reply</span>
            <span className="text-xs ml-1 text-muted-foreground">
              {comment.replies?.length || 0}
            </span>
          </Button>
        )}
        {comment.paragraphIsOwner && (
          <div className="border-l h-6 flex items-center my-auto border-muted-foreground opacity-70 px-2 gap-2">
            <EditCommentButton
              onClick={() => handleSetIsEditingCommentId(comment.id)}
              isDisabled={isEditInProgress}
            />
            <DeleteCommentButton commentId={comment.id} refresh={refresh} />
          </div>
        )}
        {/* For Farcaster comments only, we have an external link button so you can click to go to the cast directly. */}
        {comment.platform === "farcaster" && comment.farcasterCastUrl && (
          <Link
            href={comment.farcasterCastUrl}
            target="_blank"
            className="flex items-center"
          >
            <Button variant="ghost" className="px-3 py-2 my-auto ">
              <ExternalLink className="w-4 h-4 text-muted-foreground opacity-70" />
              <span className="sr-only">Go to cast</span>
            </Button>
          </Link>
        )}
        {/* <Button variant="ghost" className="px-2">
          <Share className="w-4 h-4 text-muted-foreground opacity-70" />
          <span className="sr-only">Share</span>
        </Button> */}
      </div>

      {isEditingCommentId === comment.id && (
        <div className="my-2 w-full">
          <EditComment
            comment={comment}
            onSubmitStart={() => handleEditStart()}
            onSuccess={() => {
              handleEditEnd()
              handleSetIsEditingCommentId(comment.id)
            }}
          />
        </div>
      )}

      {isReplyingToCommentId === comment.id && (
        <div className="my-2 w-full">
          <AddComment
            postId={postId}
            replyToCommentId={comment.id}
            onSubmitStart={() => handleReplyStart()}
            onSuccess={() => {
              handleReplyEnd()
              handleSetIsReplyingToCommentId(comment.id)
            }}
          />
        </div>
      )}
    </CardFooter>
  )
}
